import React, { useState, useEffect, useCallback, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import SiteLayout from "@/components/SiteLayout";
import { img } from "@/lib/image";
import analytics from "@/lib/analytics";

const slides = [
  {
    n: "01",
    title: "Glitch Game Labs",
    subtitle: "Client Pitch Deck",
    image: "/pitch-deck/1.webp",
  },
  {
    n: "02",
    title: "Who Are We?",
    subtitle: "Game Industry Folk, Best in their field",
    image: "/pitch-deck/2.webp",
  },
  {
    n: "03",
    title: "Studios We've Partnered With",
    subtitle: "Dirtcube Interactive · Triple Tap Games · Ash Dev",
    image: "/pitch-deck/3.webp",
  },
  {
    n: "04",
    title: "What Clients Say",
    subtitle: "High Quality · Best Code · Fast Delivery",
    image: "/pitch-deck/4.webp",
  },
  {
    n: "05",
    title: "Work Showcase 1",
    subtitle: "Block & color puzzle games",
    image: "/pitch-deck/5.webp",
  },
  {
    n: "06",
    title: "Work Showcase 2",
    subtitle: "Wool Runner · Yarns · loop puzzles",
    image: "/pitch-deck/6.webp",
  },
  {
    n: "07",
    title: "Work Showcase 3",
    subtitle: "Project Ignition",
    image: "/pitch-deck/7.webp",
  },
  {
    n: "08",
    title: "Work Showcase 4",
    subtitle: "Find & clean-up hidden-object games",
    image: "/pitch-deck/8.webp",
  },
  {
    n: "09",
    title: "Work Showcase 5",
    subtitle: "Match & merge games",
    image: "/pitch-deck/9.webp",
  },
  {
    n: "10",
    title: "Why Us?",
    subtitle: "Full Transparency · Affordable Pricing · Full Support",
    image: "/pitch-deck/10.webp",
  },
  {
    n: "11",
    title: "Do's & Don'ts",
    subtitle: "We focus on casual, hybrid-casual and puzzle games",
    image: "/pitch-deck/11.webp",
  },
  {
    n: "12",
    title: "Our Pricing",
    subtitle: "Game Dev $15-25/Hr · Game Design $10/Hr · Consultation $15/Hr",
    image: "/pitch-deck/12.webp",
  },
  {
    n: "13",
    title: "Thank You!",
    subtitle: "Looking forward to working with you",
    image: "/pitch-deck/13.webp",
  },
];

const FRAME_STYLE = {
  background: "rgba(255,255,255,0.03)",
  border: "1.5px solid rgba(200,255,42,0.7)",
  borderRadius: "20px",
  boxShadow:
    "0 0 0 6px rgba(6,6,6,0.6), 0 0 40px rgba(200,255,42,0.25), 0 0 120px rgba(42,214,255,0.10)",
};

const PitchDeckPage = () => {
  const [current, setCurrent] = useState(0);
  const [currentLoaded, setCurrentLoaded] = useState(false);
  const total = slides.length;

  const prev = useCallback(() => setCurrent((c) => (c - 1 + total) % total), [total]);
  const next = useCallback(() => setCurrent((c) => (c + 1) % total), [total]);

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [prev, next]);

  // Per-slide dwell tracking. The ref holds the currently-viewed slide and the
  // wall-clock time it was entered, so we can finalize it (emit a `slide` event)
  // whenever the deck advances, the tab hides, or the page unmounts.
  const slideRef = useRef({ index: 0, n: slides[0].n, enteredAt: Date.now() });

  const finalizeSlide = useCallback(() => {
    const { index, n, enteredAt } = slideRef.current;
    const elapsed = Date.now() - enteredAt;
    analytics.trackSlide(index, n, elapsed);
    // Reset the timer to "now" so a subsequent finalize (e.g. unmount right
    // after a hide) can't double-count the same dwell — the sub-250ms guard in
    // trackSlide drops the near-zero residual.
    slideRef.current = { ...slideRef.current, enteredAt: Date.now() };
  }, []);

  // When `current` changes, bank the time on the slide we just left, then reset
  // the entered timestamp for the new slide.
  useEffect(() => {
    const ref = slideRef.current;
    if (ref.index !== current) {
      finalizeSlide();
      slideRef.current = {
        index: current,
        n: slides[current].n,
        enteredAt: Date.now(),
      };
    }
  }, [current, finalizeSlide]);

  // Finalize the active slide when the tab is hidden so dwell isn't lost on
  // navigation away; restart the timer when it returns to the foreground.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        finalizeSlide();
      } else {
        slideRef.current = {
          ...slideRef.current,
          enteredAt: Date.now(),
        };
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      // On unmount, finalize whatever slide is currently being viewed.
      finalizeSlide();
    };
  }, [finalizeSlide]);

  const slide = slides[current];
  const slideSrc = img(slide.image, { w: 1600, fm: "webp", q: 82 });

  // Reset loaded flag whenever slide changes so the background preload waits
  // for the new current to finish before warming the rest.
  useEffect(() => {
    setCurrentLoaded(false);
  }, [current]);

  // After the current slide finishes, warm the browser cache with every other
  // slide in the background. Uses Image() so it doesn't render or block.
  useEffect(() => {
    if (!currentLoaded) return;
    const handles = slides
      .map((s, i) => (i === current ? null : s))
      .filter(Boolean)
      .map((s) => {
        const i = new Image();
        i.src = img(s.image, { w: 1600, fm: "webp", q: 82 });
        return i;
      });
    return () => {
      // Best effort cancel — browsers will continue, but freeing references
      // lets GC release any aborted decode work.
      handles.forEach((h) => {
        h.src = "";
      });
    };
  }, [currentLoaded, current]);

  return (
    <SiteLayout withFooter={false} autoBackdrop>
      <div className="flex flex-col items-center justify-center h-[calc(100vh-64px)] px-4 md:px-6 py-4 overflow-hidden">
        {/* Tight cluster: title row → slide → nav row, sized to the slide */}
        <div className="flex flex-col items-center w-full" style={{ maxWidth: "1331px" }}>
          {/* Slide title + counter — sits right above the slide */}
          <div className="flex items-center gap-4 mb-5 shrink-0">
            <span
              className="text-[10px] tracking-[0.4em] uppercase font-bold"
              style={{ color: "#c8ff2a" }}
            >
              Slide {slide.n} / {total}
            </span>
            <span className="text-[11px] text-white/50 tracking-wide">
              {slide.title}
            </span>
          </div>

          {/* Slide image */}
          <div
            className="relative mx-auto flex items-center justify-center cursor-pointer w-full"
            onClick={next}
          >
            <div
              className="relative inline-block"
              style={{
                maxWidth: "100%",
                // Leave headroom for title (~24px + 12px margin) and nav (~44px + 16px margin) + safety
                maxHeight: "calc(100vh - 64px - 9rem)",
                ...FRAME_STYLE,
              }}
            >
              <img
                src={slideSrc}
                alt={slide.title}
                className="block"
                decoding="async"
                fetchpriority="high"
                onLoad={() => setCurrentLoaded(true)}
                style={{
                  maxWidth: "100%",
                  maxHeight: "calc(100vh - 64px - 9rem)",
                  width: "auto",
                  height: "auto",
                  borderRadius: "inherit",
                }}
              />

              {/* Corner ticks */}
              <span
                className="absolute -top-1.5 -left-1.5 w-5 h-5 pointer-events-none"
                style={{
                  borderTop: "2px solid #c8ff2a",
                  borderLeft: "2px solid #c8ff2a",
                }}
              />
              <span
                className="absolute -top-1.5 -right-1.5 w-5 h-5 pointer-events-none"
                style={{
                  borderTop: "2px solid #ff2a8f",
                  borderRight: "2px solid #ff2a8f",
                }}
              />
              <span
                className="absolute -bottom-1.5 -left-1.5 w-5 h-5 pointer-events-none"
                style={{
                  borderBottom: "2px solid #ff2a8f",
                  borderLeft: "2px solid #ff2a8f",
                }}
              />
              <span
                className="absolute -bottom-1.5 -right-1.5 w-5 h-5 pointer-events-none"
                style={{
                  borderBottom: "2px solid #c8ff2a",
                  borderRight: "2px solid #c8ff2a",
                }}
              />

              {/* Hover overlay arrows */}
              <button
                onClick={(e) => { e.stopPropagation(); prev(); }}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity"
                style={{ background: "rgba(0,0,0,0.6)", border: "1px solid rgba(255,255,255,0.2)" }}
                aria-label="Previous slide"
              >
                <ChevronLeft size={20} className="text-white" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); next(); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity"
                style={{ background: "rgba(0,0,0,0.6)", border: "1px solid rgba(255,255,255,0.2)" }}
                aria-label="Next slide"
              >
                <ChevronRight size={20} className="text-white" />
              </button>
            </div>
          </div>

          {/* Bottom navigation — sits right below the slide */}
          <div className="flex items-center gap-6 mt-6 shrink-0">
            <button
              onClick={prev}
              className="w-11 h-11 rounded-full border border-white/20 flex items-center justify-center text-white/70 hover:text-white hover:border-[#c8ff2a] transition-colors"
              aria-label="Previous slide"
            >
              <ChevronLeft size={20} />
            </button>

            {/* Dots */}
            <div className="flex items-center gap-2">
              {slides.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrent(i)}
                  className="w-2.5 h-2.5 rounded-full transition-all"
                  style={{
                    background: i === current ? "#c8ff2a" : "rgba(255,255,255,0.2)",
                    transform: i === current ? "scale(1.4)" : "scale(1)",
                  }}
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </div>

            <button
              onClick={next}
              className="w-11 h-11 rounded-full border border-white/20 flex items-center justify-center text-white/70 hover:text-white hover:border-[#c8ff2a] transition-colors"
              aria-label="Next slide"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>
    </SiteLayout>
  );
};

export default PitchDeckPage;
